import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";

/**
 * 랜딩 헤더.
 *
 * 다크 히어로 위에서는 투명 + 흰 로고, 스크롤하면 흰 배경 + 컬러 로고로 바뀝니다
 * (design/landing-and-marketing.md §2 의 "투명 → 스크롤 시 불투명" 규칙).
 * 색 전환 자체는 LandingStyles 의 `.site-header.scrolled` 가 처리하므로
 * 여기서는 클래스만 토글합니다.
 */
export default function LandingHeader({ onPlayOpen }: { onPlayOpen: () => void }) {
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll(); // 새로고침으로 중간 지점에 복원됐을 때를 위해 초기 1회
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`site-header${scrolled ? " scrolled" : ""}`}>
      <div className="shell">
        <a
          className="logo"
          href="/"
          aria-label="해봄 홈"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          {/* eslint-disable @next/next/no-img-element */}
          {/* 2026-09-05 해봄 로고. 스크롤 전(다크 히어로 위)은 dark 판, 스크롤 후(흰 헤더)는 light 판. 크기는 기존 DQ 로고와 같은 높이 24px(60×24). */}
          <img
            className="logo-color"
            src="/images/haebom/haebom-horizontal.webp"
            alt="해봄"
            width={60}
            height={24}
          />
          <img
            className="logo-white"
            src="/images/haebom/haebom-horizontal-dark.webp"
            alt="해봄"
            width={60}
            height={24}
          />
          {/* eslint-enable @next/next/no-img-element */}
        </a>

        <div className="header-actions">
          {/*
            서비스 소개서(PDF). public/downloads 에 두어 정적 빌드 산출물(out/)에 그대로 실립니다.
            파일명은 ASCII 로 저장하고(한글·공백·대괄호는 URL 인코딩/CDN 에서 말썽), 사용자가
            받을 때 보이는 이름만 download 속성으로 한글로 돌려줍니다.
          */}
          <a
            className="link-guide"
            href="/downloads/dq-service-guide-2026.pdf"
            download="2026_해봄 서비스 소개서.pdf"
            aria-label="서비스 소개서 다운로드"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M7 1v9.5M3 7l4 4 4-4M1.5 13h11"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {/*
              화면이 좁아질수록 단계적으로 줄입니다 (CSS 참고).
              넓음: "서비스 소개서 다운" → 좁음: "소개서 다운" → 더 좁음: 아이콘만.
              텍스트가 사라져도 이름은 위 aria-label 이 남깁니다.
            */}
            <span className="guide-text">
              <span className="guide-long">서비스 </span>소개서 다운
            </span>
          </a>
          {/* 참여자 유형 모달로 가로챕니다. JS 가 죽어도 링크로 홈에는 갈 수 있게 href 유지. */}
          <a
            className="link-cta"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              onPlayOpen();
            }}
          >
            해봄 체험하기
          </a>
          <a
            className="link-login"
            href="/login"
            onClick={(e) => {
              e.preventDefault();
              router.push("/login");
            }}
          >
            로그인
          </a>
        </div>
      </div>
    </header>
  );
}
